import Button from "components/form/Button";
import { CloseIcon } from "icons/close/CloseIcon";
import { SettingsValue } from "types/payments";
import React, { useMemo, useState } from "react";
import { useAppSelector } from "store/hooks";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { formatContent, replaceMarkers } from "utils/HelperFunctions";
import { RootState } from "store/store";
import { useStateDisclaimer } from "hooks/useStateDisclaimer";

type DisclosureInitialProps = {
  handleAccept: () => void;
  generalMarkers: { [key: string]: string };
  onClose?: () => void;
  isOpen?: boolean;
};

// The consent line that labels the checkbox. Disclosure text authored in portal
// settings frequently ends with this same sentence, in which case rendering our
// own copy shows it to the customer twice, one line above the other.
const CONSENT_SENTENCE =
  "By submitting Your payment, You acknowledge that You have read, understood, and agree to the terms and conditions outlined in this Payment Disclosure Statement.";

const normalizeForCompare = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * The consent line belongs beside the checkbox, where the design puts it and
 * where it is the label for the control being ticked. Authored disclosure text
 * usually closes with the very same sentence, which rendered it twice, one line
 * above the other. Drop it from the end of the body only when it is an exact
 * duplicate of the label, so authored wording is otherwise untouched.
 */
const stripTrailingConsentLine = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let last = doc.body.lastElementChild;

    // Skip trailing empties (<p><br></p> and friends) that editors leave behind.
    while (last && normalizeForCompare(last.innerHTML) === "") {
      const previous = last.previousElementSibling;
      last.remove();
      last = previous;
    }

    if (
      last &&
      normalizeForCompare(last.textContent || "") ===
        normalizeForCompare(CONSENT_SENTENCE)
    ) {
      last.remove();
      return doc.body.innerHTML;
    }
    return html;
  } catch {
    return html;
  }
};
const DisclosureInitial = (props: DisclosureInitialProps) => {
  const { handleAccept, generalMarkers, onClose, isOpen = true } = props;
  const portalSettings = usePortalSettingsData();
  const [checked, setChecked] = useState<boolean>(false);
  // companyMarkers (loaded at login) carry the portal-level keys such as
  // "Support Phone" that the default disclosure text uses; the payment
  // markers only know "Company: Phone", so without them the phone came out
  // blank.
  const { paymentMarkers, companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );
  const hasPortalSettings = Object.keys(portalSettings || {}).length > 0;
  const usesStateOverride =
    portalSettings?.state_disclosure_override === SettingsValue.YES;
  const stateDisclaimer = useStateDisclaimer(
    isOpen && hasPortalSettings && usesStateOverride
  );
  const stateText = String(stateDisclaimer.data?.initial_disclosure ?? "");
  const disclosureSource =
    usesStateOverride && stateText.trim()
      ? stateText
      : (portalSettings?.state_disclosure_text ?? "");
  const isLoading =
    !hasPortalSettings ||
    (usesStateOverride &&
      isOpen &&
      (stateDisclaimer.status === "idle" ||
        stateDisclaimer.status === "loading"));

  const disclosureText = useMemo(() => {
    try {
      const replacedHtml = replaceMarkers(disclosureSource, {
        ...companyMarkers,
        ...paymentMarkers,
        ...generalMarkers,
      });

      return stripTrailingConsentLine(formatContent(replacedHtml));
    } catch (error) {
      console.log("handleUpdateContent error =====", error);
      return "";
    }
  }, [companyMarkers, disclosureSource, generalMarkers, paymentMarkers]);

  return (
    <div className="h-full w-full text-start flex flex-col relative">
      {onClose && (
        <button
          type="button"
          aria-label="Close disclosure"
          className="absolute top-0 right-0 cursor-pointer p-2 rounded-full hover:!bg-bgSecondary z-10"
          onClick={onClose}
        >
          <CloseIcon size={18} />
        </button>
      )}
      {/* flex-1 rather than a fixed 95%: the scrolling area takes whatever is
          left after the pinned consent row and the Accept button, so neither of
          them can be squeezed out. */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* 24px medium per the design — this is the largest text in the panel.
            It was 14px, which made the heading smaller than the body it
            introduced. */}
        <p className="text-txtPrimary lg:text-2xl text-xl font-medium h-fit">
          Disclosure Notice:
        </p>
        {/* min-h-0 lets this flex child shrink below its content height, which is
            what makes it an actual scroll container — without it the consent
            checkbox at the end is pushed out of view and can never be reached. */}
        <div className="flex-1 min-h-0 overflow-y-auto pt-5">
          {isLoading ? (
            <div className="h-full w-full grid place-items-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            </div>
          ) : (
            /* Must be a div, not a p: the disclosure text is authored HTML and
               routinely contains block-level tags, which a <p> cannot legally
               contain. The parser then closes the paragraph early and the
               content escapes its box.

               Styling here is type only — size, weight, colour, line height —
               to match the design. The wording, structure and numbering all
               come from the text authored in portal settings and are left
               exactly as they are. */
            <div
              id="disclosure-content"
              className="break-words text-txtParagraph text-base lg:text-lg leading-6 lg:leading-[26px]
                [&_strong]:text-txtPrimary [&_strong]:font-medium
                [&_b]:text-txtPrimary [&_b]:font-medium
                [&_ol]:list-decimal [&_ul]:list-disc
                [&_ol]:pl-6 [&_ul]:pl-6 [&_li]:pl-1 [&_li]:mb-3
                [&_ol]:mb-3 [&_ul]:mb-3"
              dangerouslySetInnerHTML={{ __html: disclosureText }}
            ></div>
          )}
        </div>
      </div>

      {/* Pinned: the consent checkbox sits outside the scrolling area so it
          stays put while the disclosure is read, and the customer never has to
          reach the bottom of a long document to find it. */}
      {!isLoading && (
        <div className="w-full flex gap-2 justify-start items-start border-t border-strokePrimary pt-4">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            aria-label={CONSENT_SENTENCE}
            className="dcdial_portal_checkbox mt-1 cursor-pointer shrink-0"
          />
          <p
            className="break-words lg:text-base text-sm leading-[26px] text-txtParagraph cursor-pointer"
            onClick={() => setChecked(!checked)}
          >
            {CONSENT_SENTENCE}
          </p>
        </div>
      )}
      <div className="w-full py-3 ">
        <Button
          text="Accept"
          className="w-full"
          onClick={handleAccept}
          isDisabled={!checked || isLoading}
          variant="variable"
        />
      </div>
    </div>
  );
};

export default DisclosureInitial;
